import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ActivityFilters } from '../types/activity';

const STORAGE_KEY = 'sparkadmin_activity_filters';

/**
 * Default filter values
 */
const DEFAULT_FILTERS: ActivityFilters = {
  search: '',
  status: 'published',
  playType: '',
  prepNeeded: '',
  duration: '',
  tagIds: [],
  transitionIds: [],
  ageGroupKeys: [],
  categoryIds: [],
  isAiGenerated: false,
  generationJobId: '',
  page: 1,
  pageSize: 25,
  sortBy: 'createdAt',
  sortOrder: 'desc',
};

/**
 * Parse filters from URL search params
 */
function parseFiltersFromURL(searchParams: URLSearchParams): Partial<ActivityFilters> {
  const filters: Partial<ActivityFilters> = {};

  const search = searchParams.get('search');
  if (search) filters.search = search;

  const status = searchParams.get('status');
  if (status && ['draft', 'published', 'archived'].includes(status)) {
    filters.status = status as ActivityFilters['status'];
  }

  const playType = searchParams.get('playType');
  if (playType && ['solo_play', 'play_together'].includes(playType)) {
    filters.playType = playType as ActivityFilters['playType'];
  }

  const prepNeeded = searchParams.get('prepNeeded');
  if (prepNeeded && ['no_prep_needed', 'prep_needed'].includes(prepNeeded)) {
    filters.prepNeeded = prepNeeded as ActivityFilters['prepNeeded'];
  }

  const duration = searchParams.get('duration');
  if (duration) {
    const parsed = parseInt(duration, 10);
    if ([10, 15, 30, 31].includes(parsed)) {
      filters.duration = parsed;
    }
  }

  const tagIds = searchParams.get('tagIds');
  if (tagIds) {
    const parsed = tagIds
      .split(',')
      .map((id) => parseInt(id, 10))
      .filter((id) => !isNaN(id));
    if (parsed.length > 0) {
      filters.tagIds = parsed;
    }
  }

  const transitionIds = searchParams.get('transitionIds');
  if (transitionIds) {
    const parsed = transitionIds
      .split(',')
      .map((id) => parseInt(id, 10))
      .filter((id) => !isNaN(id));
    if (parsed.length > 0) {
      filters.transitionIds = parsed;
    }
  }

  const ageGroupKeys = searchParams.get('ageGroupKeys');
  if (ageGroupKeys) {
    const parsed = ageGroupKeys
      .split(',')
      .filter((k) => ['2-3', '3-4', '4-5'].includes(k));
    if (parsed.length > 0) filters.ageGroupKeys = parsed;
  }

  const isAiGenerated = searchParams.get('isAiGenerated');
  if (isAiGenerated === 'true') filters.isAiGenerated = true;

  const generationJobId = searchParams.get('generationJobId');
  if (generationJobId) filters.generationJobId = generationJobId;

  const categoryIds = searchParams.get('categoryIds');
  if (categoryIds) {
    const parsed = categoryIds
      .split(',')
      .map((id) => parseInt(id, 10))
      .filter((id) => !isNaN(id) && id > 0);
    if (parsed.length > 0) {
      filters.categoryIds = parsed;
    }
  }

  const page = searchParams.get('page');
  if (page) {
    const parsed = parseInt(page, 10);
    if (parsed > 0) filters.page = parsed;
  }

  const pageSize = searchParams.get('pageSize');
  if (pageSize) {
    const parsed = parseInt(pageSize, 10);
    if (parsed > 0 && parsed <= 100) filters.pageSize = parsed;
  }
  
  const sortBy = searchParams.get('sortBy');
  if (sortBy && ['title', 'status', 'playType', 'prepNeeded', 'duration', 'createdAt'].includes(sortBy)) {
    filters.sortBy = sortBy as ActivityFilters['sortBy'];
  }
  
  const sortOrder = searchParams.get('sortOrder');
  if (sortOrder && ['asc', 'desc'].includes(sortOrder)) {
    filters.sortOrder = sortOrder as ActivityFilters['sortOrder'];
  }

  return filters;
}

/**
 * Load filters from localStorage
 */
function loadFiltersFromStorage(): Partial<ActivityFilters> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};
    
    const parsed = JSON.parse(stored);
    return parsed;
  } catch (error) {
    console.error('Failed to load filters from localStorage:', error);
    return {};
  }
}

/**
 * Save filters to localStorage
 */
function saveFiltersToStorage(filters: ActivityFilters): void {
  try {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { generationJobId, ...filtersToSave } = filters;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtersToSave));
  } catch (error) {
    console.error('Failed to save filters to localStorage:', error);
  }
}

/**
 * Convert filters to URL search params
 */
function filtersToSearchParams(filters: ActivityFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.search) params.set('search', filters.search);
  if (filters.status) params.set('status', filters.status);
  if (filters.playType) params.set('playType', filters.playType);
  if (filters.prepNeeded) params.set('prepNeeded', filters.prepNeeded);
  if (filters.duration) params.set('duration', filters.duration.toString());
  if (filters.tagIds.length > 0) params.set('tagIds', filters.tagIds.join(','));
  if (filters.transitionIds.length > 0) params.set('transitionIds', filters.transitionIds.join(','));
  if (filters.ageGroupKeys.length > 0) params.set('ageGroupKeys', filters.ageGroupKeys.join(','));
  if (filters.categoryIds.length > 0) params.set('categoryIds', filters.categoryIds.join(','));
  if (filters.isAiGenerated) params.set('isAiGenerated', 'true');
  if (filters.generationJobId) params.set('generationJobId', filters.generationJobId);
  if (filters.page > 1) params.set('page', filters.page.toString());
  if (filters.pageSize !== 25) params.set('pageSize', filters.pageSize.toString());
  if (filters.sortBy && filters.sortBy !== 'createdAt') params.set('sortBy', filters.sortBy);
  if (filters.sortOrder && filters.sortOrder !== 'desc') params.set('sortOrder', filters.sortOrder);

  return params;
}

/**
 * Custom hook for managing activity filters with URL and localStorage sync
 * Priority: URL params > localStorage > defaults
 */
export function useActivityFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filters with priority: URL > localStorage > defaults
  const [filters, setFilters] = useState<ActivityFilters>(() => {
    const urlFilters = parseFiltersFromURL(searchParams);
    const storageFilters = loadFiltersFromStorage();

    const initialFilters = {
      ...DEFAULT_FILTERS,
      ...storageFilters,
      ...urlFilters, // URL takes precedence
    };

    return initialFilters;
  });

  // Sync URL params to localStorage when they change
  useEffect(() => {
    const urlFilters = parseFiltersFromURL(searchParams);
    
    // If URL has any filter params, update localStorage
    if (Object.keys(urlFilters).length > 0) {
      const mergedFilters = { ...DEFAULT_FILTERS, ...urlFilters };
      saveFiltersToStorage(mergedFilters);
      
      // Update state if different
      setFilters(mergedFilters);
    }
  }, [searchParams]);

  /**
   * Update a single filter
   */
  const updateFilter = useCallback(
    (key: keyof ActivityFilters, value: ActivityFilters[keyof ActivityFilters]) => {
      setFilters((prev) => {
        const updated = { ...prev, [key]: value };
        
        // Reset to page 1 when filters change (except when changing page itself)
        if (key !== 'page') {
          updated.page = 1;
        }

        // Update URL and localStorage
        const params = filtersToSearchParams(updated);
        setSearchParams(params, { replace: true });
        saveFiltersToStorage(updated);

        return updated;
      });
    },
    [setSearchParams]
  );

  /**
   * Update multiple filters at once
   */
  const updateFilters = useCallback(
    (updates: Partial<ActivityFilters>) => {
      setFilters((prev) => {
        const updated = { ...prev, ...updates };
        
        // Reset to page 1 if not explicitly setting page
        if (!('page' in updates)) {
          updated.page = 1;
        }

        // Update URL and localStorage
        const params = filtersToSearchParams(updated);
        setSearchParams(params, { replace: true });
        saveFiltersToStorage(updated);

        return updated;
      });
    },
    [setSearchParams]
  );

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setSearchParams({}, { replace: true });
    saveFiltersToStorage(DEFAULT_FILTERS);
  }, [setSearchParams]);

  /**
   * Clear a specific filter
   */
  const clearFilter = useCallback(
    (key: keyof ActivityFilters) => {
      updateFilter(key, DEFAULT_FILTERS[key]);
    },
    [updateFilter]
  );

  /**
   * Check if any filters are active (excluding page/pageSize)
   */
  const hasActiveFilters =
    filters.search !== '' ||
    filters.status !== DEFAULT_FILTERS.status ||
    filters.playType !== '' ||
    filters.prepNeeded !== '' ||
    filters.duration !== '' ||
    filters.tagIds.length > 0 ||
    filters.transitionIds.length > 0 ||
    filters.ageGroupKeys.length > 0 ||
    filters.categoryIds.length > 0 ||
    filters.isAiGenerated === true ||
    Boolean(filters.generationJobId);

  return {
    filters,
    updateFilter,
    updateFilters,
    clearFilters,
    clearFilter,
    hasActiveFilters,
  };
}
